import { call, put } from 'redux-saga/effects';
import {
  Types,
  ILongSteelOrdersResponse,
  EOrderTab,
  IOpenOrderData,
  ITrackingFlatSteel,
  IBilledOrderTracking,
  IBilledOrderData,
  IQCFileResponse,
  IDownloadNFEResponse,
  ITrackingLongSteel,
  ICgCertificateResponse,
  CgCertificateStatus
} from './types';
import {
  loadLongSteelOrdersSuccess,
  loadLongSteelOrdersFailure,
  loadTrackingFlatSteelSuccess,
  loadTrackingFlatSteelFailure,
  loadBilledOrderTrackingSuccess,
  loadBilledOrderTrackingFailure,
  loadQCFileSuccess,
  loadQCFileFailure,
  loadNFFileSuccess,
  loadNFFileFailure,
  loadShippingNoteFileSuccess,
  loadShippingNoteFileFailure,
  loadTrackingLongSteelSuccess,
  loadTrackingLongSteelFailure,
  cgQcRequestSuccess,
  cgQcRequestFailure
} from './actions';
import { LongSteelOrdersService } from '../../../services/LongSteelOrders';
import { CustomersService } from '../../../services/Customers';
import { ICustomer } from '../customers/types';
import { loadCustomerFromIdSuccess } from '../customers/actions';
import { ECustomerTypeView } from '../user/types';
import { addToast } from '../toast/actions';
import { EToastTypes } from '../toast/types';

interface ILoadOrdersParams {
  payload: {
    states: string[];
    customerIds: string[];
    customerViewType: ECustomerTypeView;
    tab: EOrderTab;
    startDate: string;
    endDate: string;
    needToLoadCustomer?: boolean;
  };
  type: Types;
}

interface ILoadOrderTrackingParams {
  payload: {
    openOrderItem: IOpenOrderData;
    billedOrderItem: IBilledOrderData;
  };
  type: Types;
}

interface ILoadOrderLongSteelTrackingParams {
  payload: {
    openOrderItemId: string;
    customerId: string;
    salesOrder: number;
    salesOrderItem: number;
  };
  type: Types;
}

interface ILoadCgQualityCertificateParams {
  type: Types;
  payload: {
    billedOrderItem: IBilledOrderData;
  };
}

export function* loadLongSteelOrdersFromLocalStorageSaga() {
  try {
    const ordersService = new LongSteelOrdersService();
    const ordersResponse: ILongSteelOrdersResponse = yield call(
      ordersService.getOrdersFromLocalStorage.bind(ordersService)
    );
    yield put(loadLongSteelOrdersSuccess(ordersResponse));
  } catch (err) {
    console.error('loadLongSteelOrdersFromLocalStorageSaga', err);
    yield put(loadLongSteelOrdersFailure(err));
  }
}

export function* loadLongSteelOrdersSaga(params: ILoadOrdersParams) {
  const {
    states,
    customerIds,
    customerViewType,
    tab,
    startDate,
    endDate,
    needToLoadCustomer
  } = params.payload;
  if (needToLoadCustomer) {
    try {
      const customerService = new CustomersService();
      const customer: ICustomer = yield customerService.getCustomerById(
        customerIds[0]
      );
      yield put(loadCustomerFromIdSuccess(customer));
    } catch (err) {
      yield put(loadLongSteelOrdersFailure(err, true));
      return;
    }
  }

  try {
    const ordersService = new LongSteelOrdersService();
    const ordersResponse: ILongSteelOrdersResponse =
      yield ordersService.getOrders(
        states,
        customerIds,
        customerViewType,
        tab,
        startDate,
        endDate
      );

    yield put(loadLongSteelOrdersSuccess(ordersResponse));
  } catch (err) {
    console.error('loadOrdersSaga', err);
    yield put(loadLongSteelOrdersFailure(err));
    yield put(
      addToast({
        message: 'Falha ao carregar os pedidos.',
        type: EToastTypes.DANGER
      })
    );
  }
}

export function* loadTrackingFlatSteelSaga(params: ILoadOrderTrackingParams) {
  const { openOrderItem } = params.payload;
  try {
    const ordersService = new LongSteelOrdersService();

    const orderTracking: ITrackingFlatSteel = yield call(
      ordersService.getTrackingFlatSteel.bind(ordersService),
      openOrderItem
    );

    yield put(loadTrackingFlatSteelSuccess(openOrderItem, orderTracking));
  } catch (err) {
    console.error('loadTrackingFlatSteelSaga', err);
    yield put(loadTrackingFlatSteelFailure(openOrderItem, err));
  }
}

export function* loadTrackingLongSteelSaga(
  params: ILoadOrderLongSteelTrackingParams
) {
  const { openOrderItemId, customerId, salesOrder, salesOrderItem } =
    params.payload;
  try {
    const ordersService = new LongSteelOrdersService();

    const orderTracking: ITrackingLongSteel = yield call(
      ordersService.getTrackingLongSteel.bind(ordersService),
      customerId,
      salesOrder,
      salesOrderItem
    );

    yield put(loadTrackingLongSteelSuccess(openOrderItemId, orderTracking));
  } catch (err) {
    console.error('loadTrackingLongSteelSaga', err);
    yield put(loadTrackingLongSteelFailure(openOrderItemId, err));
  }
}

export function* loadBilledOrderTrackingSaga(params: ILoadOrderTrackingParams) {
  const { billedOrderItem } = params.payload;
  try {
    const ordersService = new LongSteelOrdersService();

    const billedOrderTracking: IBilledOrderTracking = yield call(
      ordersService.getBilledOrderTracking.bind(ordersService),
      billedOrderItem
    );

    yield put(
      loadBilledOrderTrackingSuccess(billedOrderItem, billedOrderTracking)
    );
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(loadBilledOrderTrackingFailure(billedOrderItem, err));
  }
}

export function* loadQCFileSaga(params: ILoadOrderTrackingParams) {
  const { billedOrderItem } = params.payload;
  try {
    const ordersService = new LongSteelOrdersService();

    const { cqNumber, deliveryCode, deliveryItemCode } = billedOrderItem;

    const qcFileResponse: IQCFileResponse = yield call(
      ordersService.loadQCFile.bind(ordersService),
      cqNumber,
      deliveryCode,
      deliveryItemCode
    );

    yield put(loadQCFileSuccess(billedOrderItem, qcFileResponse));
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(loadQCFileFailure(billedOrderItem, err));
  }
}

export function* loadNFFileSaga(params: ILoadOrderTrackingParams) {
  const { billedOrderItem } = params.payload;
  try {
    const ordersService = new LongSteelOrdersService();

    const downloadNFEResponse: IDownloadNFEResponse = yield call(
      ordersService.loadNFEFile.bind(ordersService),
      billedOrderItem.danfeNumber
    );

    yield put(loadNFFileSuccess(billedOrderItem, downloadNFEResponse));
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(loadNFFileFailure(billedOrderItem, err));
  }
}

export function* loadShippingNoteFileSaga(params: ILoadOrderTrackingParams) {
  const { billedOrderItem } = params.payload;
  try {
    const ordersService = new LongSteelOrdersService();

    const downloadNFEResponse: IDownloadNFEResponse = yield call(
      ordersService.loadShippingNoteFile.bind(ordersService),
      billedOrderItem.deliveryDocumentZvoi
    );
    yield put(
      loadShippingNoteFileSuccess(billedOrderItem, downloadNFEResponse)
    );
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(loadShippingNoteFileFailure(billedOrderItem, err));
  }
}

export function* loadCgQualityCertificateSaga(
  params: ILoadCgQualityCertificateParams
) {
  const { billedOrderItem } = params.payload;
  try {
    const ordersService = new LongSteelOrdersService();
    const response: ICgCertificateResponse = yield call(
      ordersService.solicitateCgCertificate.bind(ordersService),
      billedOrderItem
    );
    window.localStorage.removeItem('@egerdau-web/long-steel-orders');
    if (response.ok) {
      yield put(cgQcRequestSuccess(billedOrderItem, response));
      if (
        billedOrderItem.billingHistory.statusQC ===
        CgCertificateStatus.Processing
      ) {
        yield put(
          addToast({
            message:
              'Pedido de certificado recebido! Você será notificado por e-mail após o processamento.',
            type: EToastTypes.INFO,
            durationInMS: 5000
          })
        );
      }
    } else {
      throw new Error('Request not ok');
    }
  } catch (err) {
    yield put(
      addToast({
        message: `Não foi possível baixar o pedido ${billedOrderItem.billingNumber} pois este não possui certificado disponível`,
        type: EToastTypes.WARNING,
        durationInMS: 5000
      })
    );
    yield put(cgQcRequestFailure(params.payload.billedOrderItem));
  }
}
